import { gridStringOrNumberComparator } from '@mui/x-data-grid-pro';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { EmptyCell } from '@/components/tables/DataTable/renderers/cell/EmptyCell';
import { IconRenderer } from '@/components/tables/DataTable/renderers/cell/IconRenderer';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { EMPLOYEE_TIER_DISPLAY_MAP } from '@/modules/employees/constants';
import { EmployeeTier, EmployeeWhereInput } from '@/types/schema';

import { RowData } from './AdminUsersPage.types';
import { EmployeeFragment } from './graphql/AdminUsers.generated';

export function getAdminUsersWhere({
  search,
  roleIds,
  tiers,
  teamIds,
}: {
  search: string;
  roleIds: string[];
  tiers: EmployeeTier[];
  teamIds: string[];
}): EmployeeWhereInput {
  const terms = search.trim().split(/\s+/).filter(Boolean);

  return {
    // Each term can match the full name or the email address.
    and: terms.map((term) => ({
      hasUserWith: [
        {
          or: [{ searchNameContainsFold: term }, { emailContainsFold: term }],
        },
      ],
    })),
    ...(roleIds.length > 0 && { hasRolesWith: [{ idIn: roleIds }] }),
    ...(tiers.length > 0 && { tierIn: tiers }),
    ...(teamIds.length > 0 && { hasTeamsWith: [{ idIn: teamIds }] }),
  };
}

/**
 * Maps an employee tier to its display string
 */
export function getEmployeeTierDisplayName(tier?: EmployeeTier | null): string {
  if (!tier) return EMPLOYEE_TIER_DISPLAY_MAP[EmployeeTier.Advanced];
  return EMPLOYEE_TIER_DISPLAY_MAP[tier];
}

export function mapDataToRows(
  employees: EmployeeFragment[]
): Omit<RowData, 'icon'>[] {
  return employees.map((employee) => {
    const clientNames = compact(
      employee.assignedHouseholds?.map((client) => client.displayName) ?? []
    );

    const roleNames = employee.roles?.map((role) => role.displayName) ?? [];

    const teamNames = employee.teams?.map((team) => team.name) ?? [];

    const userType = getEmployeeTierDisplayName(employee.tier ?? null);

    return {
      id: employee.id ?? '',
      user: {
        lineOne: employee.displayName ?? '',
        lineTwo: employee.user.email ?? '',
      },
      clientNames: clientNames.join(', '),
      roleNames: roleNames.join(', '),
      teamNames: teamNames.length > 0 ? teamNames.join(', ') : null,
      scimManaged: Boolean(employee.workosDirectoryUserID),
      userType,
    };
  });
}

export const useUserTableColumns = () => {
  return useMemo(() => {
    const columns: Column<RowData>[] = compact([
      {
        field: 'user',
        headerName: 'User',
        flex: 1,
        renderCell: TwoLineTextRenderer({
          lineOneProps: {
            variant: 'title-xs',
          },
          rightContent: ({ row }) => {
            if (row.scimManaged) {
              return (
                <Badge variant={BadgeVariants.SecondaryFilled} display="SCIM" />
              );
            }
            return null;
          },
        }),
        sortComparator: (v1: RowData['user'], v2: RowData['user'], p1, p2) => {
          return gridStringOrNumberComparator(v1.lineOne, v2.lineOne, p1, p2);
        },
      },
      {
        field: 'roleNames',
        headerName: 'Roles',
        flex: 1,
        renderCell: TextRenderer(),
      },
      {
        field: 'userType',
        headerName: 'Type',
        flex: 1,
        renderCell: TextRenderer(),
      },
      {
        field: 'teamNames',
        headerName: 'Teams',
        flex: 1,
        renderCell: TextRenderer({
          text: ({ row }) => row.teamNames ?? <EmptyCell />,
        }),
      },
      {
        field: 'clientNames',
        headerName: 'Client access',
        flex: 1,
        renderCell: TextRenderer(),
      },
      {
        field: 'icon',
        align: 'center',
        renderCell: IconRenderer({
          icon: Edit02Icon,
        }),
        sortable: false,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
      },
    ]);

    return columns;
  }, []);
};
